import { roomRingsValid } from "./room-rings.js";
import { z } from "zod";
import { trimCaptureValid } from './trim-capture.js';
const Point = z.object({ x: z.number().finite(), y: z.number().finite() });
const LooseObject = z.record(z.string(), z.unknown());
const CapturedTrimRecord = LooseObject.superRefine((record, ctx) => {
    const trim = record;
    if (!trim.measurementSource)
        return; // Old estimates remain round-trippable until reviewed.
    if (!trimCaptureValid(trim)) {
        ctx.addIssue({ code: 'custom', message: 'Confirmed trims require purpose, location and positive finite measured lengths; Other requires a note.' });
        return;
    }
    if (trim.segments?.length && Math.abs(trim.segments.reduce((n, s) => n + s.lengthMM, 0) - Number(trim.lengthMM)) > 0.001)
        ctx.addIssue({ code: 'custom', message: 'Trim length must equal the sum of measured segments.' });
});
export const FactualAssessmentMeasure = z.object({
    id: z.string().min(1),
    axis: z.enum(["x", "y"]),
    label: z.string().default(""),
    // Swift Codable omits nil optional dimensions. Absence is unmeasured, never zero.
    lengthMM: z.number().finite().nullable().optional(),
    pxRun: z.number().finite().nonnegative(),
    a: Point.optional(),
    b: Point.optional(),
}).passthrough();
export const FactualAssessmentArea = z.object({
    id: z.string().min(1),
    name: z.string().default("Room"),
    boundary: z.array(Point).max(500).default([]),
    boundaryHoles: z.array(z.array(Point).min(3).max(500)).max(300).optional(),
    doorways: z.array(LooseObject).max(200).default([]),
    trims: z.array(CapturedTrimRecord).max(300).default([]),
    measures: z.array(FactualAssessmentMeasure).max(300).default([]),
}).passthrough().refine(a => !a.boundaryHoles?.length || roomRingsValid(a.boundary, a.boundaryHoles), { message: "Room holes must be simple, disjoint and strictly inside the shell" });
export const FactualAssessmentFloor = z.object({
    trimRuns: z.array(CapturedTrimRecord).max(300).optional(),
    id: z.string().min(1),
    name: z.string().default("Whole site"),
    areas: z.array(FactualAssessmentArea).max(300).default([]),
    walls: z.array(LooseObject).max(800).default([]),
    stairs: z.array(LooseObject).max(100).default([]),
    registers: z.array(LooseObject).max(100).default([]),
    scaleMmPerPx: z.number().finite().positive().nullable().optional(),
}).passthrough();
/**
 * The factual Mobile/AIW site assessment. Unknown fields are retained so a
 * newer client can round-trip through an older one without losing evidence.
 * The contract version lives beside the assessment in WorkDocument; it is not
 * injected into Mobile's AssessmentV15 bytes.
 */
export const FactualAssessment = z.object({
    jobRef: z.string().default(""),
    address: z.string().nullable().optional(),
    rooms: z.array(LooseObject).default([]),
    floors: z.array(FactualAssessmentFloor).max(20).default([]),
    siteNote: z.string().default(""),
}).passthrough();
export const AssessmentRevisionBinding = z.object({
    work_id: z.string().uuid(),
    revision_id: z.string().uuid(),
    content_hash: z.string().regex(/^[a-f0-9]{64}$/),
});
//# sourceMappingURL=factual-assessment.js.map